// Blog post rendering shared by the publish-handoff export (backend) and the in-app content preview
// (frontend), so the preview shows exactly what the export hands the site agent. Pure functions only:
// no Node or DOM APIs. Moved from backend/src/services/publish-handoff/format.ts (PRD-0190).

const MEDIA_REF = /\[\[media:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]\]/gi;
const SLUG_MAX = 80;
const DESCRIPTION_MAX = 160;

/*
 * Round-3 P1-4: internal sourcing notes must never reach readers. A standalone note paragraph
 * ("*Editorial evidence note: ...*") is moved out of the draft; see content-quality/blog.ts.
 */
const NOTE_LEAD = /^(?:[>*_\-\s]|\d+\.\s)*(?:\*\*|__)?\s*(?:editorial|internal|research|evidence|sourcing|source)(?:\s+(?:evidence|source|sourcing|research))?\s+notes?\s*(?:\*\*|__)?\s*:/i;

export type InternalNoteStrip = { draft: string; notes: string[] };

/** Moves standalone sourcing-note paragraphs out of the draft (never edits other text). */
export function stripInternalNotes(draft: string): InternalNoteStrip {
  const blocks = draft.split(/\n{2,}/);
  const kept: string[] = [];
  const notes: string[] = [];
  for (const block of blocks) {
    if (NOTE_LEAD.test(block.trim())) notes.push(block.trim().replace(/^[>*_\s]+|[*_\s]+$/g, "").slice(0, 2_000));
    else kept.push(block);
  }
  return { draft: kept.join("\n\n").trim(), notes };
}

/** Stable URL slug from a title: ascii, lowercase, hyphenated, at most 80 characters. */
export function slugify(title: string): string {
  const base = title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!base) return "post";
  if (base.length <= SLUG_MAX) return base;
  const cut = base.slice(0, SLUG_MAX);
  const lastHyphen = cut.lastIndexOf("-");
  return (lastHyphen > 20 ? cut.slice(0, lastHyphen) : cut).replace(/-+$/g, "");
}

/** A front-matter-safe value: one line, no wrapping quotes, never parsed as a tag array. */
export function singleLine(value: string): string {
  let text = value.replace(/[\r\n\t\u2028\u2029]+/g, " ").replace(/\s{2,}/g, " ").trim();
  for (let previous = ""; previous !== text;) {
    previous = text;
    text = text.replace(/^["'\s]+|["'\s]+$/g, "");
    if (text.startsWith("[") && text.endsWith("]")) text = text.slice(1, -1);
  }
  return text;
}

/** Drops the "Status:/Channel:/Audience:" review chips some drafts carry above a `---` line. */
function stripReviewChips(content: string): string {
  const lines = content.split(/\r?\n/);
  let cursor = 0;
  while (cursor < lines.length && /^(Status|Channel|Audience):\s*.+$/i.test(lines[cursor] ?? "")) cursor += 1;
  if (cursor === 0 || (lines[cursor] ?? "").trim() !== "---") return content;
  return lines.slice(cursor + 1).join("\n");
}

function normalizedHeading(value: string): string {
  return value.replace(/[*_`#]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** Reader-facing body: chips, internal notes and a leading H1 that repeats the title are removed. */
export function readerBody(content: string, title: string): string {
  const withoutChips = stripReviewChips(content.replace(/\r\n/g, "\n"));
  let body = stripInternalNotes(withoutChips).draft.trim();
  const firstLine = /^#\s+(.+)\n?/.exec(body);
  if (firstLine && normalizedHeading(firstLine[1]!) === normalizedHeading(title)) body = body.slice(firstLine[0].length).trim();
  return body;
}

/** Markdown → plain text (media tokens, images and formatting removed). */
export function plainText(markdown: string): string {
  return markdown
    .replace(MEDIA_REF, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/(\*\*|__|\*|_|~~)/g, "")
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** SEO description: the first prose paragraph, at most 160 characters, cut on a word boundary. */
export function seoDescription(body: string): string {
  const paragraph = body.split(/\n{2,}/).map((block) => block.trim())
    .find((block) => block && !/^(#{1,6}\s|```|[-*+]\s|\d+\.\s|>|!\[)/.test(block)) ?? body;
  const text = singleLine(plainText(paragraph));
  if (text.length <= DESCRIPTION_MAX) return text;
  const cut = text.slice(0, DESCRIPTION_MAX - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.-]+$/g, "")}\u2026`;
}

export function wordCount(body: string): number {
  return plainText(body).split(/\s+/).filter(Boolean).length;
}

/** `https://brand.example/blog/<slug>` from the brand site's origin; null without a usable site. */
export function canonicalSuggestion(siteUrl: string | null | undefined, slug: string): string | null {
  const origin = siteOrigin(siteUrl);
  return origin ? `${origin}/blog/${slug}` : null;
}

/** `https://brand.example` from a brand's website setting (scheme optional); null when unusable. */
export function siteOrigin(siteUrl: string | null | undefined): string | null {
  if (!siteUrl?.trim()) return null;
  try {
    const parsed = new URL(siteUrl.trim().includes("://") ? siteUrl.trim() : `https://${siteUrl.trim()}`);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return parsed.origin;
  } catch {
    return null;
  }
}

export { MEDIA_REF };

// ── Minimal, escaping Markdown → HTML ────────────────────────────────────────

export function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function safeHref(raw: string): string | null {
  const href = raw.trim();
  // Browsers treat `\` like `/`, so `/\evil.example` would become a protocol-relative external URL.
  if (href.includes("\\") || /[\u0000-\u001f]/.test(href)) return null;
  if (href.startsWith("/") && !href.startsWith("//")) return href;
  try {
    const parsed = new URL(href);
    return parsed.protocol === "https:" || parsed.protocol === "http:" || parsed.protocol === "mailto:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function inline(text: string): string {
  // Code spans, images and link tags are stashed behind placeholders so the emphasis pass never
  // touches them: `_`/`*` inside URLs (utm_source=..., image_paths) must not become <em>.
  const stash: string[] = [];
  const hold = (html: string) => {
    stash.push(html);
    return `\u0000${stash.length - 1}\u0000`;
  };
  let html = escapeHtml(text).replace(/`([^`]+)`/g, (_m, code: string) => hold(`<code>${code}</code>`));
  html = html.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt: string, src: string) => {
    const href = safeHref(src.replace(/&amp;/g, "&"));
    return href ? hold(`<img src="${escapeHtml(href)}" alt="${alt}">`) : alt;
  });
  html = html.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, target: string) => {
    const href = safeHref(target.replace(/&amp;/g, "&"));
    // Only the opening tag (with the URL) is held; the label still gets emphasis.
    return href ? `${hold(`<a href="${escapeHtml(href)}">`)}${label}</a>` : label;
  });
  html = html.replace(/(\*\*|__)(.+?)\1/g, "<strong>$2</strong>").replace(/(\*|_)(.+?)\1/g, "<em>$2</em>");
  // Held items can contain other placeholders (a code span in an image's alt text): restore until none remain.
  for (let pass = 0; pass < 3 && html.includes("\u0000"); pass += 1) {
    html = html.replace(/\u0000(\d+)\u0000/g, (_m, index: string) => stash[Number(index)] ?? "");
  }
  return html;
}

/** Every text run is HTML-escaped; links and images only keep http(s), mailto and site-relative URLs. */
export function markdownToHtml(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index]!;
    if (!line.trim()) { index += 1; continue; }
    if (line.trim().startsWith("```")) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index]!.trim().startsWith("```")) code.push(lines[index++]!);
      index += 1;
      out.push(`<pre><code>${escapeHtml(code.join("\n"))}</code></pre>`);
      continue;
    }
    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      out.push(`<h${heading[1]!.length}>${inline(heading[2]!.trim())}</h${heading[1]!.length}>`);
      index += 1;
      continue;
    }
    const list = /^\s*([-*+]|\d+\.)\s+/.exec(line);
    if (list) {
      const ordered = /\d/.test(list[1]!);
      const items: string[] = [];
      while (index < lines.length) {
        const item = /^\s*([-*+]|\d+\.)\s+(.*)$/.exec(lines[index]!);
        if (!item || /\d/.test(item[1]!) !== ordered) break;
        items.push(`<li>${inline(item[2]!)}</li>`);
        index += 1;
      }
      out.push(ordered ? `<ol>${items.join("")}</ol>` : `<ul>${items.join("")}</ul>`);
      continue;
    }
    if (line.startsWith(">")) {
      const quote: string[] = [];
      while (index < lines.length && lines[index]!.startsWith(">")) quote.push(lines[index++]!.replace(/^>\s?/, ""));
      out.push(`<blockquote><p>${inline(quote.join(" ").trim())}</p></blockquote>`);
      continue;
    }
    const paragraph: string[] = [];
    while (index < lines.length && lines[index]!.trim()
      && !/^(#{1,6}\s|```|\s*([-*+]|\d+\.)\s|>)/.test(lines[index]!)) paragraph.push(lines[index++]!.trim());
    out.push(`<p>${inline(paragraph.join(" "))}</p>`);
  }
  return out.join("\n");
}

// ── Hero image (preview == export) ───────────────────────────────────────────

export interface HeroImageRef {
  /** Site-relative (`/blog-media/<slug>/hero.png`) or absolute http(s) URL. */
  src: string;
  alt: string;
}

/**
 * Site-relative URL for an export image's repo path: `frontend/public/blog-media/x/hero.png` →
 * `/blog-media/x/hero.png` (files under a static `public/` directory are served from the site root).
 * Returns null when the path has no `public/` segment (or nothing after it): the site URL is then
 * unknown, and callers omit the hero line rather than emit a URL that may not resolve.
 */
export function publicPathFor(suggestedPath: string): string | null {
  const path = suggestedPath.trim().replace(/\\/g, "/").replace(/^\.?\/+/, "");
  const marker = /(^|\/)public\//.exec(path);
  if (!marker) return null;
  const rest = path.slice(marker.index + marker[0].length).replace(/^\/+/, "");
  return rest ? `/${rest}` : null;
}

/** A one-line Markdown image whose alt text cannot break out of the `![...]` brackets. */
export function heroImageMarkdown(hero: HeroImageRef): string {
  const alt = hero.alt.replace(/[\r\n\t\u2028\u2029]+/g, " ").replace(/[[\]\\]/g, "").replace(/\s{2,}/g, " ").trim();
  const src = hero.src.trim().replace(/[\s()]/g, (char) => encodeURIComponent(char));
  return `![${alt}](${src})`;
}

/** The reader body with the selected hero as its first line (sites like ThinkRun keep images in the body). */
export function bodyWithHero(body: string, hero: HeroImageRef | null | undefined): string {
  if (!hero?.src.trim()) return body;
  return body ? `${heroImageMarkdown(hero)}\n\n${body}` : heroImageMarkdown(hero);
}
